import { useCallback, useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * useEntitlement — small cache of `/api/me/subscription` for gating UI.
 *
 * Returns { data, loading, refresh, isPremium, hasEntitlement }.
 * Never blocks rendering — while loading, `isPremium` is false and any
 * premium-gated UI reveals its calm locked state. On error the user is
 * treated as free (safe default). NEVER trust `isPremium` as security;
 * backend is authoritative — this is UI presentation only.
 */
const useEntitlement = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('rbr_token');
    if (!token) { setLoading(false); setData(null); return; }
    try {
      const r = await fetch(`${API_URL}/api/me/subscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setData(await r.json());
      else if (r.status === 404) setData({ status: 'NONE', isPremium: false, entitlements: [] });
      else setData({ status: 'NONE', isPremium: false, entitlements: [] });
    } catch {
      setData({ status: 'NONE', isPremium: false, entitlements: [] });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const isPremium = !!(data && data.isPremium);
  const hasEntitlement = useCallback(
    (k) => !!(data && (data.entitlements || []).includes(k)) || isPremium,
    [data, isPremium],
  );

  return { data, loading, refresh, isPremium, hasEntitlement };
};

export default useEntitlement;
